import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { makeRouter } from '@adornis/router/client/make-router.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import logo from '@chancenlogistik/assets/logo_chancenlogistik.png';
import '@chancenlogistik/client/pages/logistik-page-cart.js';
import '@chancenlogistik/client/pages/logistik-page-order-status.js';
import '@chancenlogistik/client/pages/logistik-page-order-success.js';
import '@chancenlogistik/client/pages/logistik-page-product-details.js';
import { LogistikFirstLevelPage, LogistikRoutes, LogistikSecondLevelPage } from '@chancenlogistik/client/routes.js';
import { LearningMaterialAPI } from '@chancenwerk/db/api/LearningMaterialAPI.js';
import { LogistikProductSetAPI } from '@chancenwerk/db/api/LogistikProductSetAPI.js';
import { LogistikLearningMaterialCategory, LogistikProductCategory } from '@chancenwerk/db/enums/LogistikEnums.js';
import { LogistikSchoolAccessTokenAPI } from '@chancenwerk/db/tokens.js';
import type { ChromiumInput } from '@chancenwerk/design/components/cr-input.js';
import '@chancenwerk/design/components/cr-loading-state.js';
import { orderRouter } from '@chancenwerk/logistik/client/pages/logistik-page-order-status.js';
import { type ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import '@chancenwerk/pages/chancen-page-impressum-privacy.js';
import { watch } from '@lit-labs/preact-signals';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { of, switchMap } from 'rxjs';
import './components/hefte-component-product-overview.js';
import './components/hefte-component-sidebar.js';
import { activeToken } from './globalSubjects.js';
import './pages/hefte-page-cart.js';
import './pages/hefte-page-checkout.js';
import './pages/hefte-page-landing.js';
import './pages/hefte-page-product-details.js';

const router = makeRouter(
  {
    root: {
      path: '/',
      render: stateful(() => {
        const user = useObservable(() => currentUser<ChancenwerkUser>(), []);
        if (user) {
          goTo(LogistikRoutes.landing);
          return html`<hefte-page-landing></hefte-page-landing>`;
        }
        return html`<hefte-page-landing></hefte-page-landing>`;
      }),
    },
    'restricted-landing': {
      path: `/${LogistikFirstLevelPage.RESTRICTED_LANDING}`,
      render: () => html`<hefte-page-landing></hefte-page-landing>`,
    },
    'order-status': {
      path: '/order/status*',
      render: () => html`<logistik-page-order-status ${css({ height: '100%' })}></logistik-page-order-status>`,
      subRouter: orderRouter,
    },
    cart: {
      path: `/${LogistikFirstLevelPage.ORDER}/${LogistikSecondLevelPage.CART}`,
      render: () => html`<hefte-page-cart ${css({ height: '100%' })}></hefte-page-cart>`,
    },
    checkout: {
      path: `/${LogistikFirstLevelPage.ORDER}/${LogistikSecondLevelPage.CHECKOUT}`,
      render: () => html`<hefte-page-checkout ${css({ height: '100%' })}></hefte-page-checkout>`,
    },
    'order-success': {
      path: `/${LogistikFirstLevelPage.ORDER}/${LogistikSecondLevelPage.SUCCESS}`,
      render: () => html`<logistik-page-order-success ${css({ height: '100%' })}></logistik-page-order-success>`,
    },
    'product-details': {
      path: `/${LogistikFirstLevelPage.ORDER}/${LogistikSecondLevelPage.ITEM}/:id`,
      render: ({ id }) => html`<hefte-page-product-details .selectedProductID=${id}></hefte-page-product-details>`,
    },
    'learning-materials': {
      path: `/${LogistikFirstLevelPage.ORDER}/${LogistikSecondLevelPage.LEARNING_MATERIALS}`,
      render: () => html`
        <hefte-component-product-overview
          heading="CHANCENhefte"
          .categories=${Object.values(LogistikLearningMaterialCategory)}
          .subscription=${LearningMaterialAPI.subscribePaginated}
          .orderingCategory=${LogistikProductCategory.LEARNING_MATERIAL}
        >
        </hefte-component-product-overview>
      `,
    },
    'product-sets': {
      path: `/${LogistikFirstLevelPage.ORDER}/${LogistikSecondLevelPage.PRODUCT_SETS}`,
      render: () => html`
        <hefte-component-product-overview
          heading="CHANCENsets"
          .subscription=${LogistikProductSetAPI.subscribeForService}
          .orderingCategory=${LogistikProductCategory.SET}
        >
        </hefte-component-product-overview>
      `,
    },
    privacy: {
      path: `/${LogistikFirstLevelPage.PRIVACY}`,
      render: () => html`<chancen-page-impressum-privacy></chancen-page-impressum-privacy>`,
    },
    impress: {
      path: `/${LogistikFirstLevelPage.IMPRESS}`,
      render: () => html`<chancen-page-impressum-privacy></chancen-page-impressum-privacy>`,
    },
  },
  {
    fallback: () => html`<logistik-page-not-found></logistik-page-not-found>`,
  },
).init();

@customElement('main-element')
export class MainElement extends ChemistryLitElement {
  @state() private readonly _activeToken = new RXController(this, activeToken);
  @state() private readonly _authorized = new RXController(this, this._activeToken.observable, token$ =>
    token$.pipe(
      switchMap(token => (token && !isNaN(+token) ? LogistikSchoolAccessTokenAPI.validate(+token)() : of(false))),
    ),
  );

  private readonly _codeInput = createRef<ChromiumInput>();

  override render() {
    if (!this._authorized.value) return this._authorize();
    return html`
      <cr-flex horizontal ${css({ height: '100%' })}>
        <hefte-component-sidebar></hefte-component-sidebar>
        <cr-flex flex ${css({ height: '100%', minWidth: '0', backgroundColor: this.colors.neutral.shade(50) })}>
          <cr-flex
            ${css({
              overflow: 'auto',
              height: '100%',
            })}
          >
            ${watch(router.outlet)}
          </cr-flex>
        </cr-flex>
      </cr-flex>
    `;
  }

  private _authorize() {
    return html`
      <x-flex h100 w100 space="xl">
        <x-flex crossaxis-center padding="lg" space="xl" ${css({ background: this.colors.primary })}>
          <img src=${logo} ${css({ width: '20%' })} />
          <cr-h2 ${css({ color: '#006359' })}> Willkommen in der CHANCENlogistik </cr-h2>
        </x-flex>
        <cr-flex center crossaxis-center flex>
          ${this._authorized.isLoading
            ? html` <cr-loading-state></cr-loading-state> `
            : html`
                <cr-flex crossaxis-center space="md">
                  <cr-text>
                    Geben Sie Ihren individuellen Zugangscode ein, um CHANCENhefte bei der CHANCENlogistik zu bestellen
                  </cr-text>
                  ${this._activeToken.value && !this._authorized.value
                    ? html`
                        <cr-text tone="error"> Der aktuell eingegebene Zugangscode ist nicht (mehr) gültig! </cr-text>
                      `
                    : nothing}
                  <cr-input placeholder="Zugangscode" ${ref(this._codeInput)}></cr-input>
                  <cr-button @click=${() => this._activeToken.next(this._codeInput.value?.value.value)}>
                    Weiter
                  </cr-button>
                </cr-flex>
              `}
        </cr-flex>
      </x-flex>
    `;
  }
}
